import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFaceConfused } from '@itdev-fortawesome/pro-regular-svg-icons/faFaceConfused';
import { faFaceGrinBeam } from '@itdev-fortawesome/pro-regular-svg-icons/faFaceGrinBeam';
import { faFaceLaugh } from '@itdev-fortawesome/pro-regular-svg-icons/faFaceLaugh';
import { faFaceSmile } from '@itdev-fortawesome/pro-regular-svg-icons/faFaceSmile';
import { auth } from './stores/auth.svelte';
import { goto } from '$app/navigation';
import type { Class } from './api/types';

/**
 * Figure out the home screen of the logged in user based on auth state and redirect
 */
export function userRoleRedirect() {
  if (auth.user?.redirectClassId !== undefined) {
    // if a student has only one class to load, there's no reason for them to have to look at a list of classes first
    goto(`/classes/${auth.user.redirectClassId}`, { replaceState: true });
  } else if (auth.user?.role === 'student') {
    goto('/classes', { replaceState: true });
  } else if (auth.user?.role === 'instructor') {
    goto('/instructors/classes', { replaceState: true });
  } else if (auth.user) {
    goto('/admin/classes', { replaceState: true });
  }
}

export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.src = url;
    img.onerror = rej;
    img.onload = () => {
      res(img);
    };
  });
}

const dateFormatterLong = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export function formatDate(dateStr: string | Date) {
  let date: Date;
  if (typeof dateStr === 'string') {
    date = new Date(dateStr);
  } else {
    date = dateStr;
  }
  return dateFormatterLong.format(date);
}

const dateFormatterShort = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: 'numeric',
});

export function formatDateShort(dateStr: string) {
  return dateFormatterShort.format(new Date(dateStr));
}

export function utcNowIso(): string {
  return new Date().toISOString();
}

export function proficiencyLevelLabel(level: number): string {
  switch (level) {
    case 1:
      return 'Emerging';
    case 2:
      return 'Pre-Competent';
    case 3:
      return 'Competent';
    case 4:
      return 'Expert';
    default:
      throw new Error(`Proficiency level out of bounds: ${level}`);
  }
}

export function proficiencyLevelDescription(level: number): string {
  switch (level) {
    case 1:
      return 'Fails to demonstrate safe practice consistently or demonstrates poor clinical judgement.';
    case 2:
      return 'Demonstrates foundational awareness of clinical concepts but requires consistent guidance, supervision and support to apply knowledge in practice.';
    case 3:
      return 'Demonstrates increasing independence and critical thinking in applying nursing concepts but still requires occasional supervision and reinforcement to ensure safe and effective practice.';
    case 4:
      return 'Independently and consistently applies nursing knowledge, demonstrates sound clinical judgement, and functions effectively as a beginning professional nurse with minimal supervision.';
    default:
      throw new Error(`Proficiency level out of bounds: ${level}`);
  }
}

export function proficiencyLevelIcon(level: number): IconDefinition {
  switch (level) {
    case 1:
      return faFaceConfused;
    case 2:
      return faFaceSmile;
    case 3:
      return faFaceLaugh;
    case 4:
      return faFaceGrinBeam;
    default:
      throw new Error(`Proficiency level out of bounds: ${level}`);
  }
}

export function isClassOpen(clazz: Class): boolean {
  const now = utcNowIso();
  return clazz.startDate <= now && clazz.endDate > now;
}
